"use strict";
/*
 * Copyright (C) 2021 JET PROTOCOL HOLDINGS, LLC.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Airdrop = void 0;
const staking_1 = require("../staking");
const spl_token_1 = require("@solana/spl-token");
const common_1 = require("../common");
const hooks_1 = require("../common/hooks");
const client_1 = require("./client");
const layout_1 = require("./layout");
class Airdrop {
    /**
     * Creates an instance of Airdrop.
     * @param {PublicKey} airdropAddress
     * @param {DerivedAccount} rewardsVaultAddress
     * @param {AirdropInfo} airdrop
     * @param {AssociatedToken} rewardsVault
     * @memberof Airdrop
     */
    constructor(airdropAddress, rewardsVaultAddress, airdrop, rewardsVault) {
        this.airdropAddress = airdropAddress;
        this.rewardsVaultAddress = rewardsVaultAddress;
        this.airdrop = airdrop;
        this.rewardsVault = rewardsVault;
        this.targetInfo = Airdrop.decodeTargetInfo(airdrop.targetInfo);
    }
    /**
     * Derives the reward vault for the airdrop
     *
     * @static
     * @param {PublicKey} airdrop
     * @returns {PublicKey}
     * @memberof Airdrop
     */
    static deriveRewardsVault(airdrop) {
        return (0, common_1.findDerivedAccount)(client_1.RewardsClient.PROGRAM_ID, airdrop, "vault");
    }
    /**
     * Loads the airdrop account and its reward vault
     *
     * @static
     * @param {Program<RewardsIdl>} rewardsProgram
     * @param {PublicKey} airdropAddress
     * @returns {Promise<Airdrop>}
     * @memberof Airdrop
     */
    static async load(rewardsProgram, airdropAddress) {
        const rewardsVaultAddress = this.deriveRewardsVault(airdropAddress);
        const rewardsVault = await common_1.AssociatedToken.loadAux(rewardsProgram.provider.connection, rewardsVaultAddress);
        const airdrop = (await rewardsProgram.account.airdrop.fetch(airdropAddress));
        if (!rewardsVault) {
            throw new Error("Rewards vault is undefined");
        }
        return new Airdrop(airdropAddress, rewardsVaultAddress, airdrop, rewardsVault);
    }
    /**
     * Loads the airdrop accounts associating with the stake pool vault
     *
     * @static
     * @param {Program<RewardsIdl>} rewardsProgram
     * @param {PublicKey} stakePoolVault
     * @returns {Promise<Airdrop[]>}
     * @memberof Airdrop
     */
    static async loadAll(rewardsProgram, stakePoolVault) {
        const dataSizeFilter = {
            dataSize: 400464
        };
        const stakePoolFilter = {
            memcmp: {
                offset: 8 + 32 + 32 + 32 + 8,
                bytes: stakePoolVault.toBase58()
            }
        };
        const airdropInfos = await rewardsProgram.account.airdrop.all([dataSizeFilter, stakePoolFilter]);
        const rewardVaultAddresses = airdropInfos.map(airdrop => this.deriveRewardsVault(airdrop.publicKey));
        const rewardVaults = await common_1.AssociatedToken.loadMultipleAux(rewardsProgram.provider.connection, rewardVaultAddresses);
        const airdrops = [];
        for (let i = 0; i < airdropInfos.length; i++) {
            const airdropInfo = airdropInfos[i];
            const rewardVaultAddress = rewardVaultAddresses[i];
            const rewardVault = rewardVaults[i];
            if (!rewardVault) {
                throw new Error(`Rewards vault at ${rewardVaultAddress.toBase58()} is undefined`);
            }
            const airdrop = new Airdrop(airdropInfo.publicKey, rewardVaultAddress, airdropInfo.account, rewardVault);
            airdrops.push(airdrop);
        }
        return airdrops;
    }
    static decodeTargetInfo(targetInfo) {
        return layout_1.AirdropTargetsStruct.decode(new Uint8Array(targetInfo));
    }
    /**
     * React hook to use the airdrop account and its hook
     *
     * @static
     * @param {(Program<RewardsIdl> | undefined)} rewardsProgram
     * @param {(PublicKey | undefined)} airdrop
     * @returns {Airdrop | undefined}
     * @memberof Airdrop
     */
    static use(rewardsProgram, airdrop) {
        return hooks_1.Hooks.usePromise(async () => rewardsProgram && airdrop && Airdrop.load(rewardsProgram, airdrop), [rewardsProgram, airdrop === null || airdrop === void 0 ? void 0 : airdrop.toBase58()]);
    }
    /**
     * TODO:
     *
     * @static
     * @param {(Program<RewardsIdl> | undefined)} rewardsProgram
     * @param {(PublicKey | undefined)} stakePoolVault
     * @returns {(Airdrop[] | undefined)}
     * @memberof Airdrop
     */
    static useAll(rewardsProgram, stakePoolVault) {
        return hooks_1.Hooks.usePromise(async () => rewardsProgram && stakePoolVault && Airdrop.loadAll(rewardsProgram, stakePoolVault), [rewardsProgram, stakePoolVault]);
    }
    /**
     * TODO:
     *
     * @param {PublicKey} wallet
     * @returns {(AirdropTarget | undefined)}
     * @memberof Airdrop
     */
    getRecipient(wallet) {
        return this.targetInfo.recipients.find(recipient => wallet.equals(recipient.recipient));
    }
    /**
     * TODO:
     *
     * @static
     * @param {TransactionInstruction[]} instructions
     * @param {Program<RewardsIdl>} rewardsProgram
     * @param {Airdrop} airdrop
     * @param {StakePool} stakePool
     * @param {StakeAccount} stakeAccount
     * @memberof Airdrop
     */
    static async withClaim(instructions, rewardsProgram, airdrop, stakePool, owner) {
        const addresses = staking_1.StakeAccount.deriveAccounts(stakePool.program, stakePool.addresses.stakePool, owner);
        console.log("Claiming and staking the airdrop.");
        const ix = await rewardsProgram.methods
            .airdropClaim()
            .accounts({
            /** The airdrop to claim from */
            airdrop: airdrop.airdropAddress,
            /** The token account to claim the rewarded tokens from */
            rewardVault: airdrop.rewardsVaultAddress,
            /** The address entitled to the airdrop, which must sign to claim */
            recipient: owner,
            /** The address to receive rent recovered from the claim account */
            receiver: owner,
            /** The stake pool to deposit stake into */
            stakePool: airdrop.airdrop.stakePool,
            /** The stake pool token vault */
            stakePoolVault: stakePool.vault.address,
            /** The account to own the stake being deposited */
            stakeAccount: addresses.stakeAccount,
            /** The voter weight for the stake account */
            voterWeightRecord: addresses.voterWeightRecord,
            /** The max voter weight */
            maxVoterWeightRecord: stakePool.stakePool.maxVoterWeightRecord,
            stakingProgram: staking_1.StakeClient.PROGRAM_ID,
            tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
        })
            .instruction();
        instructions.push(ix);
    }
    /**
     * TODO:
     *
     * @static
     * @param {Program<RewardsIdl>} rewardsProgram
     * @param {Airdrop} airdrop
     * @param {StakePool} stakePool
     * @param {PublicKey} owner
     * @returns {Promise<TransactionInstruction[]>}
     * @memberof Airdrop
     */
    static async claim(rewardsProgram, airdrop, stakePool, owner) {
        const ix = [];
        await this.withClaim(ix, rewardsProgram, airdrop, stakePool, owner);
        console.log("claim ix", ix);
        return ix;
    }
}
exports.Airdrop = Airdrop;
//# sourceMappingURL=airdrop.js.map